// Terms & Conditions — Twilio A2P 10DLC-compliant. The "SMS Terms &
// Conditions" section copy is required verbatim by Twilio's campaign
// reviewers. Mounted at both /terms (legacy) and /terms-and-conditions
// (Twilio-requested).
import policyDates from '@/lib/policyDates.json';

const Section = ({ title, children }) => (
  <section className="space-y-3">
    <h2
      className="text-xl sm:text-2xl font-semibold text-[#0f172a] mt-8"
      style={{ fontFamily: 'Outfit, sans-serif' }}
    >
      {title}
    </h2>
    <div className="space-y-3 text-slate-600 leading-relaxed">{children}</div>
  </section>
);

export default function TermsPage() {
  return (
    <div data-testid="terms-page" className="py-16 lg:py-24">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1
          className="text-4xl sm:text-5xl font-bold tracking-tight mb-2"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Terms &amp; Conditions
        </h1>
        <p className="text-sm text-slate-500 mb-10">
          <strong>Last Updated:</strong> {policyDates.terms}
        </p>

        <div className="prose max-w-none text-slate-600 leading-relaxed">
          <p>
            Welcome to Meet For Mahjong. These Terms &amp; Conditions
            (&ldquo;Terms&rdquo;) govern your use of our website and services.
            We have written them in plain language so they are easy to follow.
            By using the site, you agree to these Terms.
          </p>

          <Section title="1. Acceptance of Terms">
            <p>
              By creating an account, posting an event, sending a message, or
              otherwise using Meet For Mahjong, you agree to these Terms and
              our Privacy Policy. If you do not agree, please do not use the
              site.
            </p>
          </Section>

          <Section title="2. About Meet For Mahjong">
            <p>
              Meet For Mahjong is a website that helps people find American
              Mahjong events, instructors, public places to play, and other
              local players. The site lets you create an account, post and
              browse events, message other members, participate in the
              Discussion Board, and opt in to optional email or SMS reminders
              about activity that matters to you.
            </p>
          </Section>

          <Section title="3. Account Responsibility">
            <p>
              You are responsible for keeping your account information
              accurate and up to date, and for keeping your password
              confidential. You are also responsible for any activity that
              happens under your account. If you believe your account has been
              accessed by someone else, please contact us right away.
            </p>
          </Section>

          <Section title="4. Community Expectations">
            <p>
              Meet For Mahjong is a friendly community of players. By using
              the site, you agree to:
            </p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>
                Treat other members with respect. Harassment, hate speech,
                threats, or discrimination of any kind are not allowed.
              </li>
              <li>
                Post events that are real and accurate, and update them
                promptly if details change.
              </li>
              <li>
                Use the Discussion Board and direct messages for genuine
                Mahjong community conversation. No spam, scams, advertising
                unrelated to Mahjong, or attempts to deceive other users.
              </li>
              <li>
                Not attempt to impersonate another person, misuse another
                member&rsquo;s contact information, or interfere with the
                normal operation of the site.
              </li>
            </ul>
            <p>
              We may remove content, restrict access, or close accounts that
              violate these expectations.
            </p>
          </Section>

          <Section title="5. Event Listings and Instructor Profiles">
            <p>
              If you post an event or list yourself as an instructor, you are
              responsible for the accuracy of the information you provide,
              including dates, times, venue, cost, skill level, and any
              instructor credentials. Meet For Mahjong does not verify event
              details or instructor qualifications. Other members will rely on
              what you post, so please keep listings current.
            </p>
          </Section>

          <Section title="6. Payments, Memberships, and Posting Fees">
            <p>
              Some features of Meet For Mahjong require payment, including
              paid memberships (Verified Player, Instructor Advantage,
              Instructor Unlimited) and event posting fees. Payments are
              processed by a third-party payment provider (currently PayPal).
              By making a purchase you agree to the payment
              provider&rsquo;s own terms.
            </p>
            <p>
              Paid memberships may renew automatically until you cancel.
              You can cancel a recurring membership from your account
              settings. Refund requests are reviewed on a case-by-case basis
              by contacting us.
            </p>
          </Section>

          <Section title="7. SMS Terms & Conditions">
            <p>
              By opting in to receive SMS messages from Meet For Mahjong,
              users agree to receive text messages related to their account,
              event reminders, community updates, and other site-related
              notifications. Message frequency may vary. Message and data
              rates may apply.
            </p>
            <p>
              Users may opt out at any time by replying STOP to any message.
              After opting out, they may receive a confirmation message, and
              no further SMS messages will be sent unless they opt in again.
            </p>
            <p>
              For help, users can reply HELP or contact Meet For Mahjong at{' '}
              <a
                href="mailto:MeetForMahjong@gmail.com"
                className="text-[#56c6df] hover:underline"
              >
                MeetForMahjong@gmail.com
              </a>
              .
            </p>
            <p>
              Consent to receive SMS messages is not a condition of purchasing
              any goods or services.
            </p>
          </Section>

          <Section title="8. No Guarantee of Local Activity">
            <p>
              Meet For Mahjong helps members discover Mahjong activity in
              their area, but we cannot guarantee that events, instructors,
              players, public places to play, or other members will be
              available in every location. The number of nearby members and
              listings depends on the community in your area.
            </p>
          </Section>

          <Section title="9. Offline Meetups, Venues, and Member Interactions">
            <p>
              Meet For Mahjong provides the platform that helps members
              connect, but the actual games, classes, meetups, venues, and
              interactions happen offline between members. We do not run,
              chaperone, screen, or supervise any of these in-person
              activities, and we do not perform background checks on members,
              hosts, instructors, or venues.
            </p>
            <p>
              You are responsible for your own safety and judgment when
              meeting other people, attending events, or using public or
              private venues. We strongly recommend meeting first in
              well-lit, public locations, telling someone you trust about
              your plans, and using the in-app reporting tools if you ever
              feel unsafe.
            </p>
            <p>
              To the fullest extent allowed by law, Meet For Mahjong is not
              responsible for the conduct of any member, instructor, host, or
              venue, or for any injury, loss, or damage arising from
              in-person meetups, events, classes, or other interactions
              between members.
            </p>
          </Section>

          <Section title="10. Service Provided “As Is”">
            <p>
              Meet For Mahjong is provided on an &ldquo;as is&rdquo; and
              &ldquo;as available&rdquo; basis. We work hard to keep the site
              up and useful, but we do not warrant that it will be
              uninterrupted, error-free, or that any specific outcome will
              result from using the site.
            </p>
          </Section>

          <Section title="11. Changes to These Terms">
            <p>
              We may update these Terms from time to time. We will update the
              &ldquo;Last Updated&rdquo; date at the top of this page and, for
              material changes, will give notice through the site or by
              email. Continued use of the site after changes take effect
              means you accept the updated Terms.
            </p>
          </Section>

          <Section title="12. Contact">
            <p>
              For questions about these Terms or anything else about Meet For
              Mahjong, please contact us at{' '}
              <a
                href="mailto:MeetForMahjong@gmail.com"
                className="text-[#56c6df] hover:underline"
              >
                MeetForMahjong@gmail.com
              </a>
              .
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}
